<template>
  <v-dialog v-model="dialogVisible" max-width="700px" @click:outside="cancel">
    <v-card>
      <v-card-title class="primaryDialogTitle">
        Search Location
        <v-spacer />
        <v-btn icon @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-tabs v-model="activeTab" class="mb-2">
          <v-tab>
            <v-icon left>mdi-magnify</v-icon>
            Search Building
          </v-tab>
          <v-tab>
            <v-icon left>mdi-crosshairs-gps</v-icon>
            Manual GPS Input
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="pt-2">
          <!-- Search Building Tab -->
          <v-tab-item>
            <v-autocomplete
              v-model="selectedBuilding"
              :items="buildingOptions"
              :loading="buildingSearchLoading"
              :search-input.sync="buildingSearch"
              item-text="description"
              item-value="place_id"
              label="Search Building"
              placeholder="e.g. 寶城工業大廈"
              outlined
              flat
              clearable
              no-filter
              autofocus
              @change="onBuildingSelected"
            />

            <div v-if="tempGeoLocation && tempGeoLocation.lat" class="mt-2">
              <v-alert type="info" dense outlined>
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <strong>Location found:</strong><br />
                    Lat: {{ tempGeoLocation.lat.toFixed(6) }}<br />
                    Lng: {{ tempGeoLocation.lng.toFixed(6) }}
                  </div>
                  <v-btn icon small @click="openGoogleMapsPreview">
                    <v-icon>mdi-map-marker</v-icon>
                  </v-btn>
                </div>
              </v-alert>
            </div>
          </v-tab-item>

          <!-- Manual GPS Input Tab -->
          <v-tab-item>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="manualLat"
                  label="Latitude"
                  outlined
                  flat
                  type="number"
                  step="0.000001"
                  :rules="[latitudeRule]"
                  placeholder="e.g. 22.337951"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="manualLng"
                  label="Longitude"
                  outlined
                  flat
                  type="number"
                  step="0.000001"
                  :rules="[longitudeRule]"
                  placeholder="e.g. 114.198753"
                />
              </v-col>
            </v-row>

            <div v-if="isManualInputValid" class="mt-2">
              <v-alert type="info" dense outlined>
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <strong>Manual coordinates:</strong><br />
                    Lat: {{ manualLat }}<br />
                    Lng: {{ manualLng }}
                  </div>
                  <v-btn icon small @click="openGoogleMapsManual">
                    <v-icon>mdi-map-marker</v-icon>
                  </v-btn>
                </div>
              </v-alert>
            </div>

            <div class="text-caption grey--text mt-2">
              Enter GPS coordinates manually. Latitude should be between -90 and 90, Longitude
              should be between -180 and 180.
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="cancel">Cancel</v-btn>
        <v-btn color="primary" :disabled="!canConfirm" @click="confirm"> Confirm </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api';

export default {
  name: 'LocationSearchDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    currentLocation: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      activeTab: 0,
      // Search building data
      selectedBuilding: null,
      buildingOptions: [],
      buildingSearch: null,
      buildingSearchLoading: false,
      searchDebounceTimer: null,
      tempGeoLocation: null,
      // Manual input data
      manualLat: null,
      manualLng: null,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    isManualInputValid() {
      return (
        this.latitudeRule(this.manualLat) === true && this.longitudeRule(this.manualLng) === true
      );
    },
    canConfirm() {
      if (this.activeTab === 0) {
        // Search tab - need tempGeoLocation
        return this.tempGeoLocation && this.tempGeoLocation.lat;
      } else {
        // Manual tab - need valid manual input
        return this.isManualInputValid;
      }
    },
  },
  watch: {
    dialogVisible(val) {
      if (val) {
        this.reset();
        // If current location exists, populate manual input
        if (this.currentLocation && this.currentLocation.lat) {
          this.manualLat = this.currentLocation.lat;
          this.manualLng = this.currentLocation.lng;
        }
      }
    },
    buildingSearch(value) {
      // Debounce search
      clearTimeout(this.searchDebounceTimer);

      if (!value || value.length < 2) {
        this.buildingOptions = [];
        return;
      }

      this.searchDebounceTimer = setTimeout(() => {
        this.searchBuildings(value);
      }, 500);
    },
  },
  methods: {
    reset() {
      this.activeTab = 0;
      this.selectedBuilding = null;
      this.buildingOptions = [];
      this.buildingSearch = null;
      this.tempGeoLocation = null;
      this.manualLat = null;
      this.manualLng = null;
    },

    async searchBuildings(searchQuery) {
      try {
        this.buildingSearchLoading = true;
        const response = await api.get('/rml/admin/GoogleMap/autocomplete/places', {
          params: { search: searchQuery },
        });
        this.buildingOptions = response.data || [];
      } catch (error) {
        console.error('Error searching buildings:', error);
        this.buildingOptions = [];
      } finally {
        this.buildingSearchLoading = false;
      }
    },

    async onBuildingSelected(placeId) {
      if (!placeId) {
        this.tempGeoLocation = null;
        return;
      }

      try {
        // Call the geocode API to get detailed location information
        const response = await api.get(`/rml/admin/GoogleMap/geocode/${placeId}`);
        const geocodeData = response.data;

        // Update temp geo location with lat/lng
        if (geocodeData.geometry && geocodeData.geometry.location) {
          this.tempGeoLocation = {
            lat: geocodeData.geometry.location.lat,
            lng: geocodeData.geometry.location.lng,
          };
        }
      } catch (error) {
        console.error('Error fetching geocode data:', error);
        this.$emit('error', 'Failed to fetch location details');
      }
    },

    latitudeRule(value) {
      if (value === null || value === undefined || value === '') return 'Latitude is required';
      const lat = parseFloat(value);
      if (isNaN(lat)) return 'Invalid latitude';
      if (lat < -90 || lat > 90) return 'Latitude must be between -90 and 90';
      return true;
    },

    longitudeRule(value) {
      if (value === null || value === undefined || value === '') return 'Longitude is required';
      const lng = parseFloat(value);
      if (isNaN(lng)) return 'Invalid longitude';
      if (lng < -180 || lng > 180) return 'Longitude must be between -180 and 180';
      return true;
    },

    openGoogleMapsPreview() {
      if (this.tempGeoLocation) {
        window.open(
          `https://www.google.com/maps?q=${this.tempGeoLocation.lat},${this.tempGeoLocation.lng}`,
          '_blank',
        );
      }
    },

    openGoogleMapsManual() {
      if (this.isManualInputValid) {
        window.open(`https://www.google.com/maps?q=${this.manualLat},${this.manualLng}`, '_blank');
      }
    },

    confirm() {
      let location = null;

      if (this.activeTab === 0 && this.tempGeoLocation) {
        // From search
        location = { ...this.tempGeoLocation };
      } else if (this.activeTab === 1 && this.isManualInputValid) {
        // From manual input
        location = {
          lat: parseFloat(this.manualLat),
          lng: parseFloat(this.manualLng),
        };
      }

      if (location) {
        this.$emit('confirm', location);
        this.dialogVisible = false;
      }
    },

    cancel() {
      this.dialogVisible = false;
    },
  },
};
</script>
